import { useExecutionReports, useOrders } from '@src/providers';
import { useAppStateDispatch, useAppStateSelector, useAppStateStore } from '@src/providers/AppStateProvider';
import type { ExecutionReport } from '@talos/kyoko';
import {
  useGlobalToasts,
  useServiceInstantiator,
  useSocketClient,
  useUserContext,
  type Order,
  type ToastProps,
} from '@talos/kyoko';
import { noop } from 'lodash-es';
import { useCallback } from 'react';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import { openView, showFeedbackCard } from '../OMSSlice';
import { OMSView } from '../OMSView';
import { OrderService } from './OrderService';
import { unsetOrderBeingModified } from './OrderSlice';

export const useOrderService = (addToast?: (props: ToastProps) => void): OrderService => {
  const store = useAppStateStore();
  const isPoppedOut = useAppStateSelector(state => state.OMS.popoutGroups.OrderForms != null);
  const webSocketClient = useSocketClient();
  const globalToasts = useGlobalToasts();
  const { keepFormOpenAfterSubmit } = useTradingSettings();
  const isModifyingOrder = useAppStateSelector(state => state.order.orderBeingModified != null);

  const { executionReportSub } = useExecutionReports();
  const { recentOrders } = useOrders();

  const dispatch = useAppStateDispatch();
  const handleOrderConfirmed = useCallback(
    (order: Order | ExecutionReport | null) => {
      if (isPoppedOut || keepFormOpenAfterSubmit) {
        // Open the feedback card. Will  show a spinner, for an execution report without OrderID
        dispatch(showFeedbackCard(order));
        if (isModifyingOrder) {
          // Clear the order form
          dispatch(unsetOrderBeingModified());
        }
      } else {
        // Just open the orders list
        dispatch(openView(OMSView.Cards));
      }
    },
    [dispatch, isPoppedOut, keepFormOpenAfterSubmit, isModifyingOrder]
  );

  const { orgApiEndpoint } = useUserContext();

  const service = useServiceInstantiator(() => {
    return new OrderService(
      store,
      webSocketClient,
      executionReportSub,
      recentOrders,
      orgApiEndpoint!,
      addToast || noop,
      globalToasts.add,
      handleOrderConfirmed
    );
  }, [
    store,
    webSocketClient,
    executionReportSub,
    recentOrders,
    orgApiEndpoint,
    addToast,
    globalToasts.add,
    handleOrderConfirmed,
  ]);

  return service;
};
